import { IMenuOption, PRODUCT_KEY } from "@opengov/components-nav-bar";
import { IntlShape } from "react-intl";

import { ControlPanelMenuItem, GlobalNavMenuItemId, GlobalNavApplicationId } from "../types";

export const BNP_MENU_ITEMS: GlobalNavApplicationId[] = [
  "budgets",
  "performance",
  "reports",
  "stories",
  "workforce",
  "initiatives",
  "transparency",
  "data",
  "workflows",
];

export const RNT_MENU_ITEMS: GlobalNavApplicationId[] = [
  "reports",
  "performance",
  "transparency",
  "data",
  "workforce",
  "stories",
  "workflows",
];

// Reporting in PSP shell — surfaces a minimal nav with just Reports and Data.
// Shared by the standalone Reporting app (passed via `reportsPublicServicePlatform`
// activeAppKey when ReportingConfig.IS_PSP_BUILD) and by the Data Manager 2.0
// PSP module, which mounts under the same shell and uses the same activeAppKey
// to inherit this nav surface.
export const REPORTING_PSP_MENU_ITEMS: GlobalNavApplicationId[] = ["reports", "data"];

// No items for the performance public service platform yet
export const PERFORMANCE_PSP_MENU_ITEMS: GlobalNavApplicationId[] = [];

export const getMenuItemIdByAppId = (globalNavAppId: GlobalNavApplicationId): GlobalNavMenuItemId => {
  switch (globalNavAppId) {
    case "dashboard":
    case "reports":
    case "reportsClassic":
    case "reportsNextGen":
    case "reportsPublicServicePlatform":
      return "reports";
    case "transparency":
      return "portal";
    case "workforce":
      return "data";
    case "performancePublicServicePlatform":
      return "performance";
    case "dataPublicServicePlatform":
      return "data";
    default:
      return globalNavAppId;
  }
};

export type ValidateMenuItemParams = {
  controlPanelMenuItem: ControlPanelMenuItem;
  entitySubdomain: string | undefined;
  entityFeaturePermissions?: string[];
};
export const validateMenuItem = ({
  controlPanelMenuItem,
  entitySubdomain,
  entityFeaturePermissions,
}: ValidateMenuItemParams) => {
  if (controlPanelMenuItem.requires_entity && !entitySubdomain) return false;
  if (
    controlPanelMenuItem.requires_feature_permission &&
    !entityFeaturePermissions?.includes(controlPanelMenuItem.requires_feature_permission)
  ) {
    return false;
  }
  return true;
};

export type GetMenuOptionsParams = {
  activeAppKey: GlobalNavApplicationId;
  activeProductKey: PRODUCT_KEY | undefined;
  entitySubdomain: string;
  /** @deprecated No longer used for workforce authorization. Will be removed in a future version. */
  entityFeaturePermissions?: string[];
  isWorkforceAuthorized?: boolean;
  intl: IntlShape;
  hideClassicReporting?: boolean;
  isFormAndWorkflowsFeatureEnabled?: boolean;
  isPerformanceManagementFeatureEnabled?: boolean;
  isWorkforcePlanning2Enabled?: boolean;
  controlPanelMenuItems: ControlPanelMenuItem[];
};

type FormatMenuItemParams = {
  appId: GlobalNavApplicationId;
  activeAppKey: GlobalNavApplicationId;
  entitySubdomain: string;
  intl: IntlShape;
  hideClassicReporting?: boolean;
  isWorkforceAuthorized?: boolean;
  isFormAndWorkflowsFeatureEnabled?: boolean;
  isPerformanceManagementFeatureEnabled?: boolean;
  controlPanelMenuItem: ControlPanelMenuItem | undefined;
};

const shouldExcludeMenuItem = (
  controlPanelMenuItem: ControlPanelMenuItem | undefined,
  entitySubdomain: string,
  entityFeaturePermissions?: string[],
): boolean => {
  if (
    controlPanelMenuItem &&
    !validateMenuItem({
      controlPanelMenuItem,
      entitySubdomain,
      entityFeaturePermissions,
    })
  ) {
    return true;
  }
  return false;
};

const shouldHideLegacyWorkforceMenuItem = (
  appId: GlobalNavApplicationId,
  isWorkforcePlanning2Enabled?: boolean,
): boolean => appId === "workforce" && Boolean(isWorkforcePlanning2Enabled);

const formatMenuItemByAppId = ({
  appId,
  activeAppKey,
  entitySubdomain,
  intl,
  hideClassicReporting,
  isWorkforceAuthorized,
  isFormAndWorkflowsFeatureEnabled,
  isPerformanceManagementFeatureEnabled,
  controlPanelMenuItem,
}: FormatMenuItemParams): IMenuOption | undefined => {
  switch (appId) {
    case "budgets": {
      return {
        label: intl.formatMessage({ defaultMessage: "Budgets" }),
        id: appId,
        tooltipText: intl.formatMessage({ defaultMessage: "View Budgets" }),
        url: `/budgets/${entitySubdomain}`,
        isActive: activeAppKey === appId,
      };
    }
    case "workforce": {
      if (!isWorkforceAuthorized) return undefined;
      return {
        label: intl.formatMessage({ defaultMessage: "Workforce" }),
        id: appId,
        tooltipText: intl.formatMessage({ defaultMessage: "View Workforce" }),
        url: `/data_manager/${entitySubdomain}/workforce`,
        isActive: activeAppKey === appId,
      };
    }
    case "reports": {
      if (hideClassicReporting) {
        return {
          label: intl.formatMessage({ defaultMessage: "Reporting" }),
          id: appId,
          tooltipText: intl.formatMessage({ defaultMessage: "View Reports" }),
          url: `/reporting/${entitySubdomain}`,
          isActive: ["reports", "reportsNextGen", "reportsPublicServicePlatform"].includes(activeAppKey),
        };
      }
      return {
        label: intl.formatMessage({ defaultMessage: "Reports" }),
        id: appId,
        tooltipText: intl.formatMessage({ defaultMessage: "View Reports" }),
        isActive: ["dashboard", "reports", "reportsClassic", "reportsNextGen", "reportsPublicServicePlatform"].includes(
          activeAppKey,
        ),
        submenuSections: [
          {
            items: [
              {
                id: `reportsNextGen`,
                label: intl.formatMessage({ defaultMessage: "Next Gen" }),
                url: `/reporting/${entitySubdomain}`,
              },
              {
                id: `reportsClassic`,
                label: intl.formatMessage({ defaultMessage: "Classic" }),
                url: `/reports#/${entitySubdomain}`,
              },
              {
                label: intl.formatMessage({ defaultMessage: "Dashboards" }),
                id: appId,
                url: `/dashboards/${entitySubdomain}`,
              },
            ],
          },
        ],
      };
    }
    case "transparency": {
      if (hideClassicReporting) return undefined;
      return {
        label: intl.formatMessage({ defaultMessage: "Transparency" }),
        id: appId,
        tooltipText: intl.formatMessage({ defaultMessage: "View Transparency" }),
        url: `/portal/${entitySubdomain}`,
        isActive: activeAppKey === appId,
      };
    }
    case "performance": {
      if (!isPerformanceManagementFeatureEnabled) return undefined;
      return {
        label: intl.formatMessage({ defaultMessage: "Performance" }),
        id: appId,
        tooltipText: intl.formatMessage({ defaultMessage: "View Performance" }),
        url: `/budgeting/${entitySubdomain}/performance`,
        isActive: activeAppKey === appId,
      };
    }
    case "initiatives": {
      if (!controlPanelMenuItem || isPerformanceManagementFeatureEnabled) return undefined;
      return {
        label: controlPanelMenuItem.name,
        id: appId,
        tooltipText: intl.formatMessage({ defaultMessage: "View {name}" }, { name: controlPanelMenuItem.name }),
        url: controlPanelMenuItem.entrypoint,
        isActive: activeAppKey === appId,
      };
    }
    case "workflows": {
      if (!isFormAndWorkflowsFeatureEnabled) return undefined;
      return {
        label: intl.formatMessage({ defaultMessage: "Workflows" }),
        id: appId,
        tooltipText: intl.formatMessage({ defaultMessage: "View Workflows" }),
        url: `/budgeting/${entitySubdomain}/workflows`,
        isActive: activeAppKey === appId,
      };
    }
    case "data": {
      if (!controlPanelMenuItem) return undefined;
      return {
        label: controlPanelMenuItem.name,
        id: appId,
        tooltipText: intl.formatMessage({ defaultMessage: "View {name}" }, { name: controlPanelMenuItem.name }),
        url: controlPanelMenuItem.entrypoint,
        isActive: ["data", "dataPublicServicePlatform"].includes(activeAppKey),
      };
    }
    default:
      if (!controlPanelMenuItem) return undefined;
      return {
        label: controlPanelMenuItem.name,
        id: appId,
        tooltipText: intl.formatMessage({ defaultMessage: "View {name}" }, { name: controlPanelMenuItem.name }),
        url: controlPanelMenuItem.entrypoint,
        isActive: activeAppKey === appId,
      };
  }
};

export const getMenuOptions = ({
  activeAppKey,
  activeProductKey,
  entitySubdomain,
  entityFeaturePermissions,
  isWorkforceAuthorized,
  hideClassicReporting,
  isFormAndWorkflowsFeatureEnabled,
  isPerformanceManagementFeatureEnabled,
  isWorkforcePlanning2Enabled,
  controlPanelMenuItems,
  intl,
}: GetMenuOptionsParams): IMenuOption[] => {
  if (!activeProductKey) return [];
  let menuItems: GlobalNavApplicationId[] = BNP_MENU_ITEMS;
  if (activeProductKey === "reporting") {
    if (activeAppKey === "reportsPublicServicePlatform" || activeAppKey === "dataPublicServicePlatform") {
      menuItems = REPORTING_PSP_MENU_ITEMS;
    } else {
      menuItems = RNT_MENU_ITEMS;
    }
  } else if (activeProductKey === "perfmgmt") {
    if (activeAppKey === "performancePublicServicePlatform") {
      menuItems = PERFORMANCE_PSP_MENU_ITEMS;
    } else {
      // Performance Management without Budget entitlement: same top-level set as R&T (no Budgets / Workforce / Initiatives).
      menuItems = RNT_MENU_ITEMS;
    }
  }

  const formattedMenuItems: (IMenuOption | undefined)[] = menuItems.map((appId) => {
    const menuItemId = getMenuItemIdByAppId(appId);
    const controlPanelMenuItem = controlPanelMenuItems.find((item) => item.id === menuItemId);
    if (
      shouldExcludeMenuItem(controlPanelMenuItem, entitySubdomain, entityFeaturePermissions) ||
      shouldHideLegacyWorkforceMenuItem(appId, isWorkforcePlanning2Enabled)
    ) {
      return undefined;
    }
    return formatMenuItemByAppId({
      appId,
      activeAppKey,
      entitySubdomain,
      intl,
      hideClassicReporting,
      isWorkforceAuthorized,
      isFormAndWorkflowsFeatureEnabled,
      isPerformanceManagementFeatureEnabled,
      controlPanelMenuItem,
    });
  });
  return formattedMenuItems.filter(Boolean) as IMenuOption[];
};
